import {
    useState,
    useCallback,
    useRef,
    createContext,
    useEffect,
    useMemo
} from "react";

export const SubviewsContext = createContext({});

const SIMPLE_MODAL_BEAVIOR = {
    open: () => true,
    close: () => false,
    isOpen: currentStatus => currentStatus,
    initialStatus: false
};

export function SubviewsOrchestratorProvider({children}) {
    const subviews = useRef({});
    const initialStatuses = useRef({});
    const [subviewsStatus, setSubviewsStatus] = useState({});

    const multiClose = useCallback(skipKey => {
        const deltaStatus = {};

        for (const [key, {close}] of Object.entries(subviews.current)) {
            if (skipKey && key !== skipKey) {
                deltaStatus[key] = close(subviewsStatus[key]);
            }
        }

        return deltaStatus;
    }, [subviewsStatus, subviews]);

    const openerFactory = useCallback((key, open) => payload => {
        const newStatus = open(payload, subviewsStatus[key]);
        const closeOtherStatus = multiClose(key);
        setSubviewsStatus({...closeOtherStatus, [key]: newStatus});
    }, [subviewsStatus, multiClose])

    const closerFactory = useCallback((key, close) => () => {
        const newStatus = close(subviewsStatus[key]);
        setSubviewsStatus(subviewsStatus => ({...subviewsStatus, [key]: newStatus}));
    }, [subviewsStatus])

    const openCheckerFactory = useCallback((key, isOpen) => () => {
        return isOpen(subviewsStatus[key]);
    }, [subviewsStatus]);


    const closeAll = () => {
        const newStatus = multiClose();
        setSubviewsStatus(newStatus);
    }

    /**
     *
     * @param props: {key, open, close, isOpen, initialStatuses}
     * @returns {[((function(*): void)|*),((function(): void)|*),*,(function(): *)]}
     */
    const registerSubview = (props)  => {
        const {open, isOpen, close, initialStatus} = {
            ...SIMPLE_MODAL_BEAVIOR,
            ...props
        }

        const { key } = props;

        subviews.current = {...subviews.current, [key]: {open, isOpen, close}};

        if (false === Object.hasOwn(subviewsStatus, key)) {
            initialStatuses.current = {...initialStatuses.current, [key]: initialStatus};
        }

        const {[key]: thisStatus} = {...initialStatuses.current, ...subviewsStatus};

        return [
            openerFactory(key, open),
            closerFactory(key, close),
            thisStatus,
            openCheckerFactory(key, isOpen)
        ];
    }

    const anyOpen = useMemo(() => {
        for (const [key, {isOpen}] of Object.entries(subviews.current)) {
            if (isOpen(subviewsStatus[key])) {
                return true;
            }
            return false;
        }
    }, [subviewsStatus])

    const isSubviewOpen = key => {
        if (!subviews.current[key]) {
            return false;
        }

        return subviews.current[key].isOpen(subviewsStatus[key]);
    }

    useEffect(() => {
        const notSetStatuses = {};

        for (const [key, init] of Object.entries(initialStatuses.current)) {
            if (false === Object.hasOwn(subviewsStatus, key)) {
                notSetStatuses[key] = init;
            }
        }

        if (Object.keys(notSetStatuses).length > 0) {
            setSubviewsStatus(subviewsStatus => ({...subviewsStatus, ...notSetStatuses}));
        }
    }, [subviewsStatus, initialStatuses])

    const openSubview = (key, payload) => {
        const entry = subviews.current[key];
        if (!entry) {
            console.warn(`[Subviews] Subview con chiave "${key}" non è registrata`);
            return;
        }
        const newStatus = entry.open(payload, subviewsStatus[key]);
        const closeOtherStatus = multiClose(key);
        setSubviewsStatus({ ...closeOtherStatus, [key]: newStatus });
    };

    const dataModel = {
        registerSubview,
        closeAll,
        anyOpen,
        isSubviewOpen,
        openSubview,
    }

    return <SubviewsContext.Provider value={dataModel}>
        {children}
    </SubviewsContext.Provider>
}